import { faCheck } from "@fortawesome/pro-solid-svg-icons";

import { toDollars } from "common/utils/formatting";
import Icon from "common/components/Icon";
import Image from "common/components/Image";
import autoshipTermsAndConditions from "common/pdfs/autoship-terms-and-conditions.pdf";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useClinic, useFeatures } from "client/clinics/hooks";
import TextButton from "client/common/components/buttons/TextButton";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { urls } from "client/routes";

import { genSubscriptionDiscountContent, genSubscriptionMaxDiscountContent } from "../../utils";
import step1 from "../../images/step-1.svg";
import step2 from "../../images/step-2.svg";
import step3 from "../../images/step-3.svg";
import dashes from "../../images/dashes.svg";

import styles from "../../styles/SubscriptionInfoDrawer.module.less";

const SubscriptionInfoDrawer = () => {
  const { close } = useCurrentDrawer();
  const clinic = useClinic();
  const isAutoshipEdb = useFeatures().AUTOSHIP_EDB;

  const saving = genSubscriptionDiscountContent(
    clinic.first_subscription_discount,
    clinic.recurring_subscription_discount,
    {
      none: () => null,
      onlyFirst: (first) => `Save up to ${first} on your first autoship`,
      onlyRecurring: (recurring) => `Save up to ${recurring} on recurring orders`,
      both: (first, recurring) =>
        `Save up to ${first} on your first autoship and up to ${recurring} on recurring orders`,
    },
  );

  const max = genSubscriptionMaxDiscountContent(
    clinic.max_first_subscription_discount,
    clinic.max_recurring_subscription_discount,
    {
      none: () => null,
      onlyFirst: (first) => ` (maximum discount of ${first} on your first order)`,
      onlyRecurring: (recurring) => ` (maximum discount of ${recurring})`,
      both: (first, recurring) =>
        ` (maximum discount of ${first} on your first order and ${recurring} on subsequent orders)`,
    },
  );

  const steps = [
    {
      label: "Step 1",
      title: "Browse items",
      description: "Find the medication or diet products you want to Autoship.",
      image: step1,
      alt: "Dog ordering",
    },
    {
      label: "Step 2",
      title: "Set frequency",
      description:
        "Select Autoship and choose your order frequency (30 days, every 3 months, etc.).",
      image: step2,
      alt: "Dog calendar",
    },
    {
      label: "Step 3",
      title: "Place order",
      description:
        "Add the item to cart and checkout! You can change, skip, or cancel later, if needed.",
      image: step3,
      alt: "Dog relaxing",
    },
  ];

  const benefits = [
    {
      label: `Free standard shipping over ${toDollars(clinic.free_shipping_over, {
        roundCents: true,
      })} and in store pickup`,
      isVisible: !!clinic.free_shipping_over,
    },
    {
      label: "Order pet prescriptions and have them delivered right to your doorstep",
    },
    {
      label: "Select Autoship frequency, so you'll never forget to reorder pet medications",
    },
    {
      label: "Flexibility to skip or cancel your deliveries any time",
    },
  ].filter(({ isVisible = true }) => isVisible);

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Why save with Autoship?" onClose={close} />
      <DrawerLayout.Content>
        {saving && (
          <div className={styles.body}>
            {saving}
            {max}. Compounded medications do not apply due to manufacturer restrictions. Additional
            exclusions may apply.
          </div>
        )}
        <div className={styles.body}>
          Simply set up your Autoship items for recurring delivery on your own schedule and we will
          have your items at your door before your pets realize you are all out of their favorite
          products!
        </div>
        <div className={styles.steps}>
          <Image alt="dashes behind dogs" src={dashes} className={styles.dashes} />
          {steps.map((step) => (
            <div className={styles.step} key={step.label}>
              <div className={styles.image}>
                <Image alt={step.alt} src={step.image} />
              </div>
              <div className={styles.content}>
                <div className={styles.label}>{step.label}</div>
                <div className={styles.title}>{step.title}</div>
                <div className={styles.description}>{step.description}</div>
              </div>
            </div>
          ))}
        </div>
        <div>
          <h6 className={styles.heading}>Why it benefits you</h6>
          <div className={styles.body}>
            {benefits.map((benefit) => (
              <div className={styles.fact} key={benefit.label}>
                <span className={styles.icon}>
                  <Icon icon={faCheck} />
                </span>
                <span className={styles.content}>{benefit.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h6 className={styles.heading}>Terms & Conditions</h6>
          <div className={styles.body}>
            By enrolling in Autoship, you agree to our Autoship Terms & Conditions. You will receive
            repeat deliveries of your product, with the ability to modify or cancel your order at
            any time. To view or change your autoship subscription, navigate to “Account” and “My
            Autoships.” For more information on our Autoship program, please view our full terms{" "}
            {isAutoshipEdb ? (
              <TextButton icon="right" dest={urls.support.autoshipTerms()} newTab={true}>
                here
              </TextButton>
            ) : (
              <TextButton icon="right" href={autoshipTermsAndConditions}>
                here
              </TextButton>
            )}
          </div>
        </div>
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

export default SubscriptionInfoDrawer;
